/**
 * Guards for probe requests Next.js answers with a 500 instead of a client error.
 *
 * `/_next/data` is the Pages Router data prefix. App Router never serves it, but
 * Next still routes it and falls through to `/_error` with a 500. Seen in prod on
 * the CloudFront origin hostname.
 *
 * Dot-file / traversal probes (`/.git/config`, `/bra/.git/./config`) also miss
 * the static-extension guard (`config` has no extension) and crash the catch-all
 * as a 500 instead of a 404.
 */

export function isNextDataPath(pathname: string): boolean {
  return pathname === '/_next/data' || pathname.startsWith('/_next/data/');
}

const PAGE_READ_METHODS = new Set(['GET', 'HEAD']);

/**
 * Page routes only implement GET and HEAD. Another method still matches the
 * App Router; Next then renders `pages/_error` and the request span is a 500.
 * Server Actions are the one POST that belongs on a page URL (`Next-Action`).
 */
export function isUnsupportedPageMethod(
  method: string,
  headers?: { get(name: string): string | null }
): boolean {
  const normalized = method.toUpperCase();
  if (PAGE_READ_METHODS.has(normalized)) return false;
  if (normalized === 'POST' && headers?.get('next-action')) return false;
  return true;
}

const ALLOWED_DOT_SEGMENTS = new Set(['.well-known']);

/**
 * True when a path segment is `.`, `..`, or a hidden file (`.git`, `.env`)
 * other than `.well-known`. Middleware should 404 these before they reach
 * `[locale]/[...slug]`.
 */
export function hasUnsafePathSegments(pathname: string): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname.split(/[?#]/)[0] || pathname);
  } catch {
    return true;
  }

  return decoded
    .split('/')
    .filter(Boolean)
    .some((segment) => {
      if (segment === '.' || segment === '..') {
        return true;
      }
      return segment.startsWith('.') && !ALLOWED_DOT_SEGMENTS.has(segment.toLowerCase());
    });
}
